import axios from "axios";
import store from '../redux';
import { showSessionExpiredModal } from '../redux/user/user.actions';

const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

export const httpMovidesk = axios.create({
  baseURL: "https://api.movidesk.com/public/v1/",
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const httpWhatsapp = axios.create({
  baseURL: process.env.REACT_APP_WHATSAPP,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

http.interceptors.request.use(
  config => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => Promise.reject(error)
);

// Flag para evitar múltiplos modais simultâneos
let isSessionExpiredModalShown = false;

/**
 * Os textos de `status` que `JwtMiddleware` (api) devolve quando recusa a
 * autenticação. São lidos aqui pelo CORPO, e não só pelo código HTTP, porque
 * api e front são deployados separadamente: existe uma janela em que a api
 * AINDA é a antiga, que respondia **HTTP 200** nesses casos. Enquanto essa
 * janela puder estar aberta, tirar a leitura do corpo seria deixar de deslogar
 * contra a api velha -- exatamente o bug que o 401 veio consertar.
 *
 * `Token is Invalid` entrou na lista agora. Ele NÃO estava antes, e por isso
 * token forjado ou corrompido contra a api antiga passava pelo interceptor
 * como SUCESSO: a tela recebia `{"status":"Token is Invalid"}` no lugar do
 * dado que pediu, sem modal e sem erro.
 */
const STATUS_DE_SESSAO_MORTA = [
  "Token is Expired",
  "Token is Invalid",
  "Authorization Token not found",
];

/**
 * O modal de sessão expirada, com a guarda de rota.
 *
 * Antes isto existia duplicado: o caminho do corpo (resposta 200) disparava o
 * modal SEM olhar a rota, e o caminho do 401 disparava COM a guarda. Ou seja,
 * a mesma negativa da api abria ou não o modal sobre a tela de login
 * dependendo de qual dos dois a produziu. Agora os dois passam por aqui, e não
 * têm como voltar a divergir.
 *
 * A guarda importa porque `/login` e `/resetar-senha` são justamente as telas
 * onde NÃO existe sessão para expirar: um modal "sua sessão expirou" ali é
 * ruído em cima de quem está tentando entrar.
 */
const dispararSessaoExpirada = () => {
  const currentPath = window.location.pathname;
  if (isSessionExpiredModalShown || currentPath === '/login' || currentPath.includes('/resetar-senha')) {
    return;
  }
  isSessionExpiredModalShown = true;
  store.dispatch(showSessionExpiredModal());
};

http.interceptors.response.use(response => {
  // `response.data` pode não ser objeto (204, corpo vazio): `?.` evita que o
  // interceptor estoure e deixe a tela carregando para sempre.
  if (STATUS_DE_SESSAO_MORTA.includes(response.data?.status)) {
    dispararSessaoExpirada();
    return Promise.reject({ errorMessage: 'Token Error', errorStatus: response.data.status });
  }
  // `?.` aqui pelo mesmo motivo da checagem acima: o teste de resposta sem
  // corpo passou a primeira linha e estourou nesta.
  if (response.data?.error === "invalid_credentials") {
    return Promise.reject({ errorMessage: 'Credential Error', errorStatus: response.data.status });
  }
  return response;
},
  error => {
    // Antes esta linha era `console.log(error.response)`, e o objeto do axios
    // carrega `config.data` -- o CORPO da requisição serializado.
    //
    // Na tela `/whatsapp` isso é vazamento de credencial: o 422 mais provável
    // de todos ali é o `phone_number_id` duplicado, e o corpo daquele `POST`
    // leva o token permanente da Meta. Ou seja, o cliente erra o cadastro uma
    // vez e o token fica em texto puro no console do navegador dele -- onde
    // qualquer extensão lê, e onde ele mesmo cola um print pedindo ajuda.
    // Mascarar o campo na tela enquanto o console imprime o valor não protege
    // coisa nenhuma.
    //
    // O diagnóstico continua: status, método e URL bastam para saber o que
    // falhou, e nenhum deles carrega segredo. Corpo de requisição e de
    // resposta ficam de fora de propósito.
    console.log(
      'HTTP',
      error?.response?.status ?? 'sem resposta',
      String(error?.config?.method || '').toUpperCase(),
      error?.config?.url || '',
    );

    // 401 = não autenticado (sessão expirada). 403 = sem permissão (não disparar modal de sessão)
    //
    // Depois do conserto do `JwtMiddleware`, é POR AQUI que passam os quatro
    // casos que antes vinham como 200 pelo handler de sucesso: token
    // inválido, expirado, usuário inexistente e token não enviado. O corpo
    // continua o mesmo (`{"status": "..."}`), só ganhou o código.
    if (error.response && error.response.status === 401) {
      dispararSessaoExpirada();
      return Promise.reject({ errorMessage: 'Unauthorized', errorStatus: 401 });
    }

    if (error.response && error.response.status === 403) {
      return Promise.reject({
        errorMessage: 'Forbidden',
        errorStatus: 403,
        responseData: error.response.data
      });
    }

    if (error.response && error.response.status === 400) {
      return Promise.reject({ errorMessage: error.response.data, errorStatus: 400 });
    }
    return Promise.reject(error);
  }
);

// Função para resetar a flag (usada após logout)
export const resetSessionExpiredFlag = () => {
  isSessionExpiredModalShown = false;
};




export default http;